/**
 * The sample order every template is previewed against.
 *
 * It is **not** catalogue data and does not pretend to be. The dish names are
 * placeholders that say so, the allergen codes carry `allergen_status:
 * 'example'` so every template prints *allergének (példa)*, and the money is
 * round. docs/overnight_constraints.md §8 and the task's own rule: never
 * invent catalogue fields, allergen data or prices; where the seed has no
 * answer, mark the gap and mock it, visibly.
 *
 * What the sample is for is the shape — §19.5 step 3 is Barna reading the
 * Hungarian, and for that the blocks, the subjects and the Összesítés have to
 * be real even when the dishes are not.
 */

import type {
  KitchenForecastDocument,
  KitchenOrderDocument,
  KitchenProductionDocument,
} from './kitchenDoc.ts'
import type { EmailPayload, OrderPayload } from './payload.ts'

/**
 * The band on every email sent while `CATALOGUE_ALLOW_UNVERIFIED` is on (Barna,
 * 10 October 2026): the test site publishes dishes whose allergens are not yet
 * verified, and an email about an order from it has to say so before anything
 * else. Hungarian first, English beside it, as the page's own notice does.
 */
export const TEST_SYSTEM_EMAIL_BANNER =
  'Tesztrendszer — az allergének ellenőrzés alatt állnak. Itt leadott rendelés nem valódi megrendelés. · ' +
  'Test system — allergens are being verified. An order placed here is not a real order.'

/** The banner `send_outbox` hands to `render`, or none. */
export const emailBanner = (catalogueUnverified: boolean): string | null =>
  catalogueUnverified ? TEST_SYSTEM_EMAIL_BANNER : null

export const SAMPLE_BANNER =
  'MINTA. Ez egy sablonelőnézet kitalált rendelési adatokkal; a fogásnevek és az allergének nem ' +
  'valódi katalógusadatok.'

const line = (
  dish_id: string,
  name_hu: string,
  price_category: OrderPayload['lines'][number]['price_category'],
  quantity: number,
  unit: string,
  allergens: { code: number; name_hu: string }[],
  is_gf_portion = false,
): OrderPayload['lines'][number] => ({
  dish_id,
  name_hu,
  name_en: null,
  price_category,
  is_gf_portion,
  is_drink: price_category === 'ital',
  quantity,
  unit,
  allergens,
  // §7.3: nothing in the interim catalogue is verified, and a preview must
  // never be the first place a reader sees allergens presented as fact.
  allergen_status: 'example',
  allergen_test_marked: true,
  gluten_free_eligible: is_gf_portion,
  station: null,
  production_units: null,
  production_day: null,
})

export function sampleOrder(overrides: Partial<OrderPayload> = {}): OrderPayload {
  const base: OrderPayload = {
    ref: 'PLT-MINTA-001',
    status: 'confirmed',
    status_reason: null,
    lane: 'card',
    lang: 'hu',
    is_business: false,
    version: 1,
    event_name: 'Minta rendezvény',
    event_date: '2026-10-16',
    event_start: '12:00',
    occasion: 'munka',
    occasion_label: 'Munkaebéd',
    guests: 40,
    gf_guests: 4,
    fulfilment: 'delivery',
    slot_id: 'slot_1',
    slot_label: '08:30–10:30',
    zone: 'A',
    postcode: '1024',
    delivery_address: null,
    customer_name: 'Kovács Anna',
    customer_email: 'minta@example.com',
    customer_company: null,
    net: 129_921,
    vat: 35_079,
    gross: 165_000,
    vat_pct: 27,
    delivery_fee: 0,
    totals_by_category: {
      sos: { quantity: 236, unit: 'db', net: 86_614, vat: 23_386, gross: 110_000 },
      edes: { quantity: 116, unit: 'db', net: 31_496, vat: 8_504, gross: 40_000 },
      gyumolcs: { quantity: 40, unit: 'adag', net: 7_874, vat: 2_126, gross: 10_000 },
      ital: { quantity: 40, unit: 'db', net: 3_937, vat: 1_063, gross: 5_000 },
    },
    payment_deadline: '2026-10-06T12:00:00+02:00',
    freeze_at: '2026-10-09T14:00:00+02:00',
    confirm_pay_deadline: null,
    created_at: '2026-10-05T09:00:00+02:00',
    in_review: false,
    public_url: null,
    lines: [
      // The codes and the names are `public.allergen_list`'s own output: the
      // statutory list of 1169/2011/EU Annex II. Which dish has which is the
      // placeholder part, and `allergen_status: 'example'` says so.
      line('MINTA-S1', 'Minta sós falat 1 (példa)', 'sos', 120, 'db', [
        { code: 1, name_hu: 'glutént tartalmazó gabonafélék' },
        { code: 11, name_hu: 'szezámmag' },
      ]),
      line('MINTA-S2', 'Minta sós falat 2 (példa)', 'sos', 92, 'db', [
        { code: 8, name_hu: 'diófélék' },
      ]),
      line('MINTA-S3', 'Minta sós falat 3 (példa)', 'sos', 24, 'db', [], true),
      line('MINTA-E1', 'Minta édes falat 1 (példa)', 'edes', 104, 'db', [
        { code: 1, name_hu: 'glutént tartalmazó gabonafélék' },
      ]),
      line('MINTA-E2', 'Minta édes falat 2 (példa)', 'edes', 12, 'db', [], true),
      line('MINTA-GY1', 'Minta gyümölcstál (példa)', 'gyumolcs', 40, 'adag', []),
      line('MINTA-I1', 'Minta ital (példa)', 'ital', 40, 'db', []),
    ],
  }
  return { ...base, ...overrides }
}

/**
 * One payload per code. The code decides which of the optional parts are
 * filled, in the same way `public.outbox_payload` decides it for a real send:
 * internal codes get `internal`, money codes get payments and invoices, the
 * callback codes get a callback and no order at all.
 */
export function samplePayload(code: string, overrides: Partial<EmailPayload> = {}): EmailPayload {
  const audience = code.startsWith('U') ? 'customer' : 'internal'
  const callbackOnly = code === 'U17' || code === 'U18' || code === 'C17'
  const order = callbackOnly ? null : sampleOrder(sampleOrderFor(code))

  const base: EmailPayload = {
    code,
    outbox_id: 0,
    audience,
    lang: 'hu',
    recipient_role: audience === 'customer' ? null : 'catering',
    to_email: audience === 'customer' ? 'minta@example.com' : 'catering@planteen.hu',
    for_date: '2026-10-16',
    created_at: '2026-10-05T09:00:00+02:00',
    order,
    internal:
      audience === 'internal' || code === 'K8'
        ? {
            review_reasons: code === 'C2' || code === 'C3' || code === 'C4' ? ['value', 'dietary_note'] : [],
            review_state: null,
            /* `build/FIXES.md` item 79 (F8): C2 is the staff notification
               for a request sent for confirmation too, "with the comment text
               and the reasons". The sample leaves both empty, so the C2
               preview and the C2 tests stay the pay-first review lane §11.4
               describes — which is the lane item 79 leaves exactly as it was.
               The confirmation shape is reached by overriding `internal`,
               which is what `test/templates.test.ts` does. */
            confirmation_reasons: [],
            customer_comment: null,
            dietary_note:
              code === 'C2' || code === 'K8' || code === 'K1'
                ? 'Egy vendégünk mogyoróallergiás. (minta szöveg)'
                : null,
            customer_phone: '+36 30 000 0000',
            is_late: code === 'K2',
            is_short_notice: code === 'K2',
            labels_blocked: code === 'C12' || code === 'K9',
            catalogue_version: 'minta',
            ruleset_version: null,
            console_url: null,
          }
        : null,
    // build/FIXES.md item 86: F8 is about a végszámla the check held -- an
    // előlegszámla that no longer adds up to the version, after a lower-price
    // change whose refund is still open. The figures are the sample's own.
    invoices: code === 'F8'
      ? [
          {
            kind: 'elolegszamla',
            szamlazz_number: 'MINTA-EL-001',
            net: 129_921,
            vat: 35_079,
            gross: 165_000,
            issued_at: '2026-10-05T09:05:00+02:00',
            status: 'issued',
            pdf_path: null,
            version: 1,
            error: null,
            reversed_at: null,
            settles: [],
          },
          {
            kind: 'vegszamla',
            szamlazz_number: null,
            net: 118_110,
            vat: 31_890,
            gross: 150_000,
            issued_at: null,
            status: 'held',
            pdf_path: null,
            version: 2,
            error: 'advances_differ,open_refund',
            reversed_at: null,
            settles: ['minta-eloleg'],
          },
        ]
      : // Document alert (20261024): F5 is about a document the Agent did
        // not issue, F9 about one it issued with no PDF. Sample figures.
        code === 'F5' || code === 'F9' || code === 'F10'
      ? [
          {
            kind: code === 'F5' ? 'szamla' : 'elolegszamla',
            szamlazz_number: code === 'F5' ? null : 'MINTA-EL-002',
            net: 129_921,
            vat: 35_079,
            gross: 165_000,
            issued_at: code === 'F5' ? null : '2026-10-05T09:05:00+02:00',
            status: code === 'F5' ? 'failed' : 'issued',
            pdf_path: null,
            version: 1,
            error: code === 'F5' ? 'szamlazz.hu nem érhető el (minta)' : null,
            reversed_at: null,
            settles: [],
          },
        ]
      : moneyCodes.has(code)
      ? [
          {
            kind: 'szamla',
            szamlazz_number: 'MINTA-SZ-001',
            net: 129_921,
            vat: 35_079,
            gross: 165_000,
            issued_at: '2026-10-05T09:05:00+02:00',
            status: 'issued',
            pdf_path: null,
          },
        ]
      : [],
    payments: moneyCodes.has(code)
      ? [
          {
            kind: 'capture',
            provider: 'simplepay',
            provider_ref: 'MINTA-REF',
            provider_txn_id: 'MINTA-TXN',
            amount: 165_000,
            currency: 'HUF',
            status: 'done',
            due_at: null,
            done_at: '2026-10-05T09:02:00+02:00',
            note: null,
          },
        ]
      : [],
    pending_change:
      code === 'U4' || code === 'U19' || code === 'U20' || code === 'C13' || code === 'C16'
        ? {
            proposed_version: 2,
            amount_due: 12_000,
            payment_ref: null,
            expires_at: '2026-10-07T12:00:00+02:00',
            status: 'awaiting_payment',
            reason: code === 'U4' ? 'dish_unavailable' : null,
          }
        : null,
    fee:
      code === 'U10' || code === 'U11'
        ? {
            base_net: 129_921,
            pct: 30,
            amount: 38_976,
            paid: 165_000,
            // Ruling 8: the balance is of money received, which is gross, so
            // the fee that comes off it is the fee's gross.
            balance: 115_500,
            waived: false,
            note: null,
            // The sample order is a consumer, so the quoted base is the
            // order's gross total and the quoted amount its gross.
            base_kind: 'gross',
            base: 165_000,
            amount_gross: 49_500,
          }
        : null,
    enquiry:
      code === 'C5'
        ? {
            id: 'minta',
            name: 'Minta Érdeklődő',
            email: 'minta@example.com',
            phone: '+36 30 000 0000',
            message: 'Szeretnénk ajánlatot kérni. (minta szöveg)',
            reasons: ['staff_or_equipment'],
            created_at: '2026-10-05T09:00:00+02:00',
          }
        : null,
    callback: callbackOnly
      ? {
          id: 'minta',
          name: 'Minta Vendég',
          phone: '+36 30 000 0000',
          email: 'minta@example.com',
          expires_at: '2026-10-08T14:00:00+02:00',
          created_at: '2026-10-05T09:00:00+02:00',
        }
      : null,
    site: {
      site_url: null,
      catering_email: 'catering@planteen.hu',
      catering_phone: '+36 30 643 9180',
      reply_to: 'catering@planteen.hu',
      bank: { name: null, account: null },
    },
    // Run 11 item 6b: the register's fee terms as supabase/seed/010_params.sql
    // holds them (templates.test.ts checks the two agree).
    terms: {
      fee_mod_after_freeze_pct: '30',
      fee_cancel_after_freeze_pct: '60',
      fee_within_window_pct: '100',
      fee_window_h: '48',
      notice_bd: '5',
      freeze_time: '14:00',
    },
    digest: sampleDigest(code),
  }
  return { ...base, ...overrides }
}

const moneyCodes = new Set([
  'U5', 'U6', 'U10', 'U11', 'U16', 'U19', 'U20',
  'C1', 'C13', 'F1', 'F2', 'F4', 'F5', 'F6', 'F7',
])

/** The one or two fields a code needs the order to be in a different state
 *  for: a transfer-lane order for the díjbekérő codes, a business buyer for
 *  one of them so the net-with-VAT side of §5.11 is previewed too. */
function sampleOrderFor(code: string): Partial<OrderPayload> {
  if (code === 'U2' || code === 'U3' || code === 'F1' || code === 'F6') {
    return { lane: 'transfer', status: 'awaiting_payment', is_business: true }
  }
  if (code === 'U7' || code === 'C7') {
    return { lane: 'transfer', status: 'cancelled', status_reason: 'payment_expired' }
  }
  if (code === 'U6') return { status: 'cancelled', status_reason: 'declined' }
  // F4 is the refund task, and §9.6 raises it after a decline, a change or a
  // cancellation. The sample left the order `confirmed` with no reason, so the
  // task's *Ok* row rendered empty — a finance email that does not say what it
  // is about. The commonest of the three is the one it shows.
  if (code === 'U11' || code === 'F4') return { status: 'cancelled', status_reason: 'customer_cancelled' }
  if (code === 'U16' || code === 'K4') return { status: 'cancelled', status_reason: 'planteen_cancelled' }
  if (code === 'U1' || code === 'C2' || code === 'C3' || code === 'C4') {
    return { status: 'in_review', in_review: true }
  }
  if (code === 'U12' || code === 'K1' || code === 'K3' || code === 'K9') return { status: 'frozen' }
  if (code === 'U13') return { status: 'delivered' }
  if (code === 'K2') return { lane: 'short_notice', status: 'frozen' }
  return {}
}

function sampleDigest(code: string): Record<string, unknown> | null {
  const rows = [
    { ref: 'PLT-MINTA-001', event_date: '2026-10-16', guests: 40, portions: 432 },
    { ref: 'PLT-MINTA-002', event_date: '2026-10-16', guests: 18, portions: 190 },
  ]
  switch (code) {
    case 'C10':
    case 'C14':
    case 'C15':
    case 'K6':
      return { rows }
    // The kitchen codes carry the document the A4 sheet prints (§11.6), so
    // the preview Barna reads in §19.5 step 3 is the document and not a
    // shorter stand-in for it. `rows` stays beside it on the two day codes,
    // because that is what a real digest carries.
    case 'K5':
      return { rows, document: sampleProductionDocument() }
    case 'K7':
      return { rows, document: sampleForecastDocument() }
    case 'K1':
    case 'K2':
    case 'K3':
    case 'K4':
    case 'K8':
      return { document: sampleOrderDocument(code) }
    case 'C11':
      return { date: '2026-10-16', committed_portions: 980, portion_cap: 1200, threshold_pct: 80 }
    case 'C12':
    case 'K9':
      return {
        catalogue_changes: [
          {
            dish: 'Minta sós falat 1 (példa)',
            field: 'allergens',
            was: '1 glutént tartalmazó gabonafélék · 11 szezámmag',
            now: '1 glutént tartalmazó gabonafélék · 8 diófélék · 11 szezámmag',
          },
        ],
      }
    case 'C17':
      return {
        basket: [
          { dish_id: 'MINTA-S1', name: 'Minta sós falat 1 (példa)', quantity: 120, unit: 'db' },
          { dish_id: 'MINTA-E1', name: 'Minta édes falat 1 (példa)', quantity: 104, unit: 'db' },
        ],
      }
    case 'A1':
      return {
        failed_code: 'U5',
        failed_outbox_id: 123,
        failed_to: 'minta@example.com',
        attempts: 5,
        error: 'minta hibaszöveg',
      }
    case 'A2':
      return { reason: 'a változás admin nélkül hagyná a rendszert', row: 'minta@example.com' }
    /* A3 (DECIDED 20261019): the allergen guard. The figures are example
       data, as every sample here is. */
    case 'A3':
      return {
        dish_id: 'PELDA',
        dish_name: 'Minta fogás',
        reason: 'a07_milk: false → true · allergen_status: verified → not_assessed',
        hold_id: 1,
        sync_run: 1,
      }
    case 'F6':
      return { received: 155_000, difference: -10_000 }
    case 'U13':
      return {
        revobox_out: 6,
        survey_token: 'mintatoken',
        survey_url: 'https://catering.planteen.hu/kerdoiv/mintatoken',
      }
    default:
      return null
  }
}

/* ------------------------------------------------------------------ *
 * The sample kitchen documents (§11.6)
 * ------------------------------------------------------------------ */

/**
 * Example data, and it says so on every line it can: the dish names carry
 * *(példa)*, `allergen_status` is `example` and `allergen_test_marked` is
 * true, so every template prints the §7.3 caveat. The station, the production
 * unit and *Adag / doboz* are null because §7.3's interim catalogue has none
 * for any dish, and a sample that filled them in would be teaching a reader to
 * expect a figure the real document cannot print.
 */
const docLine = (
  dish_id: string,
  name_hu: string,
  ordered_portions: number,
  unit: string,
  allergens: { code: number; name_hu: string }[],
  is_gf_portion = false,
  is_drink = false,
): KitchenOrderDocument['lines'][number] => ({
  dish_id,
  name_hu,
  station: null,
  is_gf_portion,
  is_drink,
  gluten_free_eligible: is_gf_portion,
  ordered_portions,
  unit,
  production_units: ordered_portions,
  production_unit: null,
  portions_per_box: null,
  production_day: '2026-10-14',
  boxes: null,
  allergens,
  allergen_status: 'example',
  allergen_test_marked: true,
})

const SAMPLE_DOC_LINES = [
  docLine('MINTA-S1', 'Minta sós falat 1 (példa)', 120, 'db', [
    { code: 1, name_hu: 'glutént tartalmazó gabonafélék' },
    { code: 11, name_hu: 'szezámmag' },
  ]),
  docLine('MINTA-S2', 'Minta sós falat 2 (példa)', 92, 'db', [{ code: 8, name_hu: 'diófélék' }]),
  docLine('MINTA-S3', 'Minta sós falat 3 (példa)', 24, 'db', [], true),
  docLine('MINTA-E1', 'Minta édes falat 1 (példa)', 104, 'db', [
    { code: 1, name_hu: 'glutént tartalmazó gabonafélék' },
  ]),
  docLine('MINTA-I1', 'Minta ital (példa)', 40, 'db', [], false, true),
]

function sampleOrderDocument(code: string): KitchenOrderDocument {
  const bands =
    code === 'K2'
      ? ['RÖVID HATÁRIDŐ']
      : code === 'K3'
      ? ['MÓDOSÍTVA']
      : code === 'K4'
      ? ['TÖRÖLVE']
      : code === 'K8' || code === 'K1'
      ? ['ÉTRENDI MEGJEGYZÉS']
      : []
  const note =
    code === 'K8' || code === 'K1' ? 'Egy vendégünk mogyoróallergiás. (minta szöveg)' : null
  return {
    kind: code as KitchenOrderDocument['kind'],
    generated_at: '2026-10-05T09:00:00+02:00',
    superseded: false,
    version: code === 'K3' ? 2 : 1,
    current_version: code === 'K3' ? 2 : 1,
    based_on_version: code === 'K3' ? 1 : null,
    version_reason: code === 'K3' ? 'Kevesebben lesznek (minta szöveg)' : null,
    bands,
    changes:
      code === 'K3'
        ? [
            {
              dish_id: 'MINTA-S1',
              name_hu: 'Minta sós falat 1 (példa)',
              unit: 'db',
              is_gf_portion: false,
              was: 160,
              now: 120,
              delta: -40,
              change: 'changed',
            },
            {
              dish_id: 'MINTA-E1',
              name_hu: 'Minta édes falat 1 (példa)',
              unit: 'db',
              is_gf_portion: false,
              was: null,
              now: 104,
              delta: 104,
              change: 'added',
            },
          ]
        : [],
    order: {
      ref: 'PLT-MINTA-001',
      status: code === 'K4' ? 'cancelled' : 'frozen',
      event_name: 'Minta rendezvény',
      event_date: '2026-10-16',
      event_start: '12:00',
      occasion_label: 'Munkaebéd',
      guests: 40,
      gf_guests: 4,
      fulfilment: 'delivery',
      slot_label: '08:30–10:30',
      zone: 'A',
      delivery_address: { postcode: '1024', town: 'Budapest', street_and_number: 'Minta utca 1.' },
      customer_name: 'Kovács Anna',
      customer_company: null,
      customer_phone: '+36 30 000 0000',
      dietary_note: note,
      labels_blocked: false,
      is_short_notice: code === 'K2',
      is_late: false,
    },
    lines: SAMPLE_DOC_LINES,
    totals: { portions: 340, gf_portions: 24, drinks: 40, boxes: 0, boxes_unknown: 5 },
    dietary_signoff:
      code === 'K1'
        ? {
            decision: 'signed',
            resolution: 'A mogyorós fogás kimarad. (minta szöveg)',
            signed_by: { name: 'Minta Aláíró', email: 'kitchen@planteen.hu' },
            at: '2026-10-05T09:30:00+02:00',
          }
        : null,
  }
}

function sampleProductionDocument(): KitchenProductionDocument {
  const dish = (
    dish_id: string,
    name_hu: string,
    portions: number,
    allergens: { code: number; name_hu: string }[],
    orders: { ref: string; portions: number; is_gf_portion?: boolean }[],
  ): KitchenProductionDocument['stations'][number]['dishes'][number] => ({
    dish_id,
    name_hu,
    unit: 'db',
    production_unit: null,
    portion_definition: null,
    is_drink: false,
    allergens,
    allergen_status: 'example',
    allergen_test_marked: true,
    portions,
    gf_portions: orders.filter((order) => order.is_gf_portion).reduce((sum, o) => sum + o.portions, 0),
    production_units: portions,
    orders: orders.map((order) => ({
      ref: order.ref,
      event_date: '2026-10-16',
      event_start: '12:00',
      guests: 40,
      portions: order.portions,
      production_units: order.portions,
      is_gf_portion: order.is_gf_portion ?? false,
      unit: 'db',
    })),
  })

  return {
    kind: 'K5',
    for_date: '2026-10-14',
    generated_at: '2026-10-13T14:00:00+02:00',
    bands: ['ÉTRENDI MEGJEGYZÉS'],
    // §7.3: no dish in the interim catalogue has a station, so the sample has
    // one group and it is the gap, named.
    stations: [
      {
        station: null,
        portions: 310,
        gf_portions: 24,
        dishes: [
          dish('MINTA-S1', 'Minta sós falat 1 (példa)', 190, [
            { code: 1, name_hu: 'glutént tartalmazó gabonafélék' },
            { code: 11, name_hu: 'szezámmag' },
          ], [
            { ref: 'PLT-MINTA-001', portions: 120 },
            { ref: 'PLT-MINTA-002', portions: 70 },
          ]),
          dish('MINTA-S3', 'Minta sós falat 3 (példa)', 24, [], [
            { ref: 'PLT-MINTA-001', portions: 24, is_gf_portion: true },
          ]),
          dish('MINTA-E1', 'Minta édes falat 1 (példa)', 96, [
            { code: 1, name_hu: 'glutént tartalmazó gabonafélék' },
          ], [
            { ref: 'PLT-MINTA-001', portions: 60 },
            { ref: 'PLT-MINTA-002', portions: 36 },
          ]),
        ],
      },
    ],
    orders: [
      {
        ref: 'PLT-MINTA-001',
        event_date: '2026-10-16',
        event_start: '12:00',
        guests: 40,
        gf_guests: 4,
        fulfilment: 'delivery',
        slot_label: '08:30–10:30',
        status: 'frozen',
        dietary_note: 'Egy vendégünk mogyoróallergiás. (minta szöveg)',
        labels_blocked: false,
        bands: ['ÉTRENDI MEGJEGYZÉS'],
      },
      {
        ref: 'PLT-MINTA-002',
        event_date: '2026-10-16',
        event_start: '11:00',
        guests: 18,
        gf_guests: 0,
        fulfilment: 'pickup',
        slot_label: null,
        status: 'frozen',
        dietary_note: null,
        labels_blocked: false,
        bands: [],
      },
    ],
    totals: { portions: 310, gf_portions: 24, drinks: 0, dishes: 3, orders: 2, stations: 1 },
  }
}

function sampleForecastDocument(): KitchenForecastDocument {
  const day = (
    date: string,
    committed_orders: number,
    committed_covers: number,
    committed_portions: number,
    extra: Partial<KitchenForecastDocument['days'][number]> = {},
  ): KitchenForecastDocument['days'][number] => ({
    date,
    holiday_name: null,
    kitchen_closed: false,
    is_clash_date: false,
    note: null,
    portion_cap: 1200,
    max_orders_per_day: 8,
    max_covers_per_day: 150,
    soft_threshold: 960,
    committed_portions,
    committed_orders,
    committed_covers,
    distinct_savoury: committed_orders === 0 ? 0 : 6,
    distinct_sweet: committed_orders === 0 ? 0 : 3,
    overridden_orders: 0,
    ...extra,
  })

  return {
    kind: 'K7',
    for_date: '2026-10-05',
    until: '2026-10-26',
    forecast_bd: 15,
    generated_at: '2026-10-05T07:00:00+02:00',
    previous_for_date: '2026-10-02',
    bands: [],
    days: [
      day('2026-10-05', 0, 0, 0),
      day('2026-10-06', 1, 18, 190),
      day('2026-10-07', 0, 0, 0),
      day('2026-10-08', 0, 0, 0),
      day('2026-10-09', 0, 0, 0),
      day('2026-10-12', 0, 0, 0),
      day('2026-10-13', 0, 0, 0),
      day('2026-10-14', 0, 0, 0),
      day('2026-10-15', 0, 0, 0),
      day('2026-10-16', 2, 58, 622, { note: null }),
      day('2026-10-19', 0, 0, 0),
      day('2026-10-20', 0, 0, 0),
      day('2026-10-21', 0, 0, 0, { is_clash_date: true }),
      day('2026-10-22', 0, 0, 0),
      day('2026-10-23', 1, 40, 980, { kitchen_closed: false }),
    ],
    dishes: [
      {
        dish_id: 'MINTA-S1',
        name_hu: 'Minta sós falat 1 (példa)',
        station: null,
        unit: 'db',
        vegleges: 120,
        visszaigazolt: 70,
        fuggo: 0,
        orders: 2,
        previous: { vegleges: 160, visszaigazolt: 70, fuggo: 0 },
        changed: true,
      },
      {
        dish_id: 'MINTA-E1',
        name_hu: 'Minta édes falat 1 (példa)',
        station: null,
        unit: 'db',
        vegleges: 104,
        visszaigazolt: 0,
        fuggo: 36,
        orders: 2,
        previous: null,
        changed: true,
      },
      {
        dish_id: 'MINTA-GY1',
        name_hu: 'Minta gyümölcstál (példa)',
        station: null,
        unit: 'adag',
        vegleges: 40,
        visszaigazolt: 0,
        fuggo: 0,
        orders: 1,
        previous: { vegleges: 40, visszaigazolt: 0, fuggo: 0 },
        changed: false,
      },
    ],
    gone: [
      {
        dish_id: 'MINTA-S9',
        name_hu: 'Minta sós falat 9 (példa)',
        station: null,
        unit: 'db',
        vegleges: 0,
        visszaigazolt: 0,
        fuggo: 0,
        orders: 0,
        previous: null,
        changed: true,
      },
    ],
    totals: { dishes: 3, days: 15, vegleges: 264, visszaigazolt: 70, fuggo: 36, orders: 4, covers: 116 },
  }
}
